import React, { useState, useEffect, useMemo, startTransition } from 'react';
import { Autocomplete, Badge, Box, Icon, Text } from '@shopify/polaris';
import { AddressAutoComplete, Prediction, useGetMapsPlaceDetail, useMutateGetGoogleMapsSuggest } from 'queries/google_maps.suggest.query';
import { SearchIcon, AlertBubbleIcon, MenuHorizontalIcon } from '@shopify/polaris-icons';
import helpers from 'helpers/index';
import { TypedAddress_suggestion } from 'queries/address_suggestion.query';
import { OptionDescriptor } from '@shopify/polaris/build/ts/src/types';
// Alias the compound TextField for Autocomplete
const { TextField: AutocompleteTextField } = Autocomplete;

function normalizePrependAddress(address: string): string {
  if (!address) return '';
  // Loại bỏ các từ khóa phổ biến và dấu câu
  return address.replace(/(thành phố|tp\.?|quận|huyện|phường|xã|thị trấn|,)/gi, '').trim();
}

function parseLatLngFromMapUrl(input: string): string | null {
  if (!input) return null;

  const value = input.trim();

  const normalizeLatLng = (lat: string, lng: string) => `${Number(lat)},${Number(lng)}`;

  // Trường hợp người dùng đã nhập "lat, lng"
  const directLatLngRegex = /^\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*$/;
  const directMatch = value.match(directLatLngRegex);
  if (directMatch) {
    return normalizeLatLng(directMatch[1], directMatch[2]);
  }

  // Chỉ parse khi là URL map phổ biến
  if (!/(google\.[a-z.]+\/maps|goo\.gl\/maps|maps\.app\.goo\.gl)/i.test(value)) return null;

  // 1) Dạng .../@10.776889,106.700806,...
  const atRegex = /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/;
  const atMatch = value.match(atRegex);
  if (atMatch) return normalizeLatLng(atMatch[1], atMatch[2]);

  // 2) Dạng ...?q=10.776889,106.700806 hoặc ...?query=...
  const queryRegex = /[?&](?:q|query|ll)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/;
  const queryMatch = value.match(queryRegex);
  if (queryMatch) return normalizeLatLng(queryMatch[1], queryMatch[2]);

  // 3) Dạng !3d10.776889!4d106.700806
  const bangRegex = /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/;
  const bangMatch = value.match(bangRegex);
  if (bangMatch) return normalizeLatLng(bangMatch[1], bangMatch[2]);

  return null;
}

interface callbackSuggestion {
  latlng: string;
  ward: string;
  province: string;
  pickup_support: number;
  position_name: string;
  extra_price: number;
  pickup_time: number;
  note: string;
}

export interface AutocompleteAddressProps extends Omit<
  React.ComponentProps<typeof AutocompleteTextField>,
  'value' | 'onChange' | 'label' | 'placeholder'
> {
  /** Current input value */
  value?: string;
  /** Text change handler */
  onChange?: (value: string) => void;
  /** Selection callback */
  selectedCallback: (prediction: callbackSuggestion | null) => void;
  /** Placeholder override */
  placeholder?: string;
  prependAddress?: string;
  latlong?: any;
  /** Label override */
  label?: string;
  address_suggestion?: TypedAddress_suggestion[];

  journey_id?: any;
}

export default function AutocompleteAddress({
  value,
  onChange,
  selectedCallback,
  placeholder = 'Tìm kiếm địa chỉ',
  label = 'Địa chỉ',
  latlong = '',
  prependAddress = '',
  address_suggestion = [],
  journey_id = null,
  ...textFieldProps
}: AutocompleteAddressProps) {
  const [inputValue, setInputValue] = useState(value ?? '');
  const [pendingValue, setPendingValue] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const { mutateAsync: loadGoogleMapsSuggestion, data: finalData } = useMutateGetGoogleMapsSuggest();
  const { mutateAsync: getLatlongfromPlaceID } = useGetMapsPlaceDetail();

  const [allOptions, setAllOptions] = useState<OptionDescriptor[]>([]);

  /** Địa chỉ gợi ý từ trong DB ra ... */
  const addressSuggestionOptions = useMemo(
    () => address_suggestion?.map((p) => ({ value: p.id, label: p.address, media: <Icon source={AlertBubbleIcon} /> })) ?? [],
    [address_suggestion]
  );
  /** // END // Địa chỉ gợi ý từ trong DB ra ... */

  const [suggestionData, setSuggestionData] = useState<AddressAutoComplete>(null);
  useEffect(() => {
    // chỉ set suggestionData khi vẫn còn chữ trong input
    if (inputValue && inputValue.trim().length > 0) {
      setSuggestionData(finalData);
    } else {
      setSuggestionData(null); // clear nếu đã xóa hết
    }
  }, [finalData, inputValue]);

  // Keep controlled value in sync
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  /** Truy vấn tới Google để lấy gợi ý */
  const debouncedFetch = useMemo(
    () =>
      helpers.debounce((keyword: string, latlong: any) => {
        loadGoogleMapsSuggestion({ address: keyword + ', ' + normalizePrependAddress(prependAddress), latlong }); // hoặc bỏ hẳn refetch, gọi API trực tiếp nếu được
      }, 1000),
    [loadGoogleMapsSuggestion, prependAddress]
  );

  useEffect(() => {
    if (pendingValue && pendingValue.length > 2) debouncedFetch(pendingValue, latlong);
  }, [latlong, pendingValue, debouncedFetch]);

  useEffect(() => {
    if (suggestionData && !helpers.isEmpty(suggestionData)) {
      /** Dữ liệu Google */
      const predictions: Prediction[] = suggestionData?.predictions ?? [];
      let options = predictions?.map((p) => ({
        value: p.place_id,
        label: (
          <p>
            <span>{p.description}</span> &nbsp;
            <Badge tone="critical">{`${(p.distance_meters / 1000).toFixed(1)} km`}</Badge>
          </p>
        ),
        media: <Icon source={MenuHorizontalIcon} />,
      }));
      /** END Dữ liệu Google */
      /** Merge dữ liệu */
      setAllOptions(options);
    }
  }, [suggestionData]);

  /** END Dữ liệu suggest */

  // Handle typing (from Autocomplete.TextField)
  // ✅ debounce riêng cho handleTextChange
  const debouncedHandleChange = useMemo(
    () =>
      helpers.debounce((text: string) => {
        // setInputValue(text);
        setPendingValue(text);
        setSelected([]);

        if (!text.trim()) {
          setSuggestionData(null);
          return;
        }

        // Khi người dùng bắt đầu gõ, clear địa chỉ cũ
        startTransition(() => {
          selectedCallback({
            latlng: '',
            ward: '',
            province: '',
            extra_price: 0,
            pickup_time: 0,
            note: '',
            pickup_support: -1,
            position_name: '',
          });
        });
      }, 600), // ← thời gian debounce (0.5s là hợp lý)
    [selectedCallback]
  );
  const handleTextChange = (text: string) => {
    if (!text.trim()) {
      setInputValue(text);
      setPendingValue('');
      setSelected([]);
      setSuggestionData(null);
      debouncedHandleChange(text);
      onChange?.(text);
      return;
    }

    const parsedLatLng = parseLatLngFromMapUrl(text);
    if (parsedLatLng) {
      setInputValue(parsedLatLng);
      setPendingValue(parsedLatLng);
      setSelected([]);
      onChange?.(parsedLatLng);
      return;
    }

    setInputValue(text);
    // setPendingValue(text);
    // setSelected([]);
    debouncedHandleChange(text);
  };

  // Handle option select
  const handleSelect = async (selectedList: string[]) => {
    const placeId = selectedList[0];
    setSelected([placeId]);

    let prediction = suggestionData?.predictions?.find((p) => p.place_id === placeId);
    if (prediction && prediction.place_id) {
      // --- START: Thay đổi ở đây ---

      /** thêm lat long khi cần thôi */

      let { lat, lng, commune, province, extra_price, pickup_time, note, pickup_support, position_name } = await getLatlongfromPlaceID({
        place_id: prediction.place_id,
        journey_id,
      });
      prediction = { ...prediction, ...{ latlng: String(lat + ',' + lng) } };

      // 1. Lấy 3 phần tử đầu tiên của mảng 'terms'
      // 2. Trích xuất giá trị 'value' từ mỗi phần tử
      // 3. Nối chúng lại thành một chuỗi, phân cách bởi dấu phẩy
      const shortAddress = prediction.description;
      // const shortAddress = prediction.terms
      //   .slice(0, 4)
      //   .map((term) => term.value)
      //   .join(', ');

      // Cập nhật ô nhập liệu và gọi onChange với địa chỉ đã được rút gọn
      setInputValue(shortAddress);
      onChange(shortAddress);

      // --- END: Thay đổi ở đây ---

      // Giữ nguyên: Gọi callback với toàn bộ object 'prediction' gốc
      selectedCallback({
        latlng: String(lat + ',' + lng),
        ward: commune,
        province,
        pickup_support,
        extra_price,
        pickup_time,
        note,
        position_name,
      });
      /** không được bỏa */
      return;
    }

    /** Nếu click từ gợi ý */
    let AddressSuggestion = address_suggestion?.find((p) => p.id === placeId);
    if (AddressSuggestion) {
      const shortAddress = AddressSuggestion.address;
      // Cập nhật ô nhập liệu và gọi onChange với địa chỉ đã được rút gọn
      setInputValue(shortAddress);
      onChange(shortAddress);
      // Giữ nguyên: Gọi callback với toàn bộ object 'prediction' gốc
      let addressData = JSON.parse(AddressSuggestion.address_data);
      selectedCallback(addressData);
      /** không được bỏa */
      return;
    }
  };

  return (
    <Autocomplete
      options={inputValue.trim() ? allOptions : addressSuggestionOptions}
      selected={selected}
      onSelect={handleSelect}
      emptyState={
        <Box padding={'200'}>
          <Text as="p" tone="subdued" alignment="center">
            {inputValue ? 'Chưa thấy địa chỉ nào trùng khớp!' : 'Gõ địa chỉ để nhận gợi ý ...'}
          </Text>
        </Box>
      }
      textField={
        <AutocompleteTextField
          {...textFieldProps}
          value={inputValue}
          onChange={handleTextChange}
          label={label}
          placeholder={placeholder}
          prefix={<Icon source={SearchIcon} />}
          autoComplete="off"
          clearButton
          onClearButtonClick={() => {
            setInputValue('');
            onChange?.('');
            setPendingValue('');
            setSelected([]);
            setSuggestionData(null);
            debouncedHandleChange('');
          }}
        />
      }
      // loading={loadingGoogleMapsSuggestion}
      listTitle=""
    />
  );
}

